// lightly adapted from: https://www.notion.so/angelstudios/API-s-For-Client-November-24-139dbf5b32db8035bd9edacd43060d2b?pvs=4#139dbf5b32db809ca9ded98c6fba687e
import React from 'react'
import { gql, useMutation } from '@apollo/client'
import { validate as validateUuid } from 'uuid'
import {
  RecommendationTrack,
  TrackRecommendationMutation,
  TrackRecommendationMutationVariables,
} from '@/types/codegen-federation'
import { useSafeTrack } from '@/utils/analytics'
import { logger } from '@/utils/logging'
import { useUser } from '../UserService'

export const TRACK_RECOMMENDATION = gql`
  mutation trackRecommendation(
    $impressions: TrackRecommendationInput!
    $presses: TrackRecommendationInput!
    $includeImpressions: Boolean!
    $includePresses: Boolean!
  ) {
    impressions: trackRecommendation(input: $impressions) @include(if: $includeImpressions)
    presses: trackRecommendation(input: $presses) @include(if: $includePresses)
  }
`

type ThrottledTracker = {
  tracked: Set<string>
  throttled: RecommendationTrack[]
}

type TrackInput = {
  type: 'IMPRESSION' | 'PRESS'
  items: {
    id: string
  }[]
}

export const useRecommendationTracking = () => {
  const track = useSafeTrack()
  const { isLoggedIn } = useUser()

  const [trackRecommendationMutation] = useMutation<TrackRecommendationMutation, TrackRecommendationMutationVariables>(
    TRACK_RECOMMENDATION,
  )

  const throttledImpressions = React.useRef<ThrottledTracker>({ tracked: new Set<string>(), throttled: [] })
  const throttledPresses = React.useRef<ThrottledTracker>({ tracked: new Set<string>(), throttled: [] })

  const flushTrackRecommendations = React.useCallback(async () => {
    if (!isLoggedIn) {
      return
    }

    if (!throttledImpressions.current.throttled.length && !throttledPresses.current.throttled.length) {
      return
    }

    const impressions = throttledImpressions.current.throttled
    throttledImpressions.current.throttled = []

    const presses = throttledPresses.current.throttled
    throttledPresses.current.throttled = []

    try {
      return await trackRecommendationMutation({
        variables: {
          impressions: {
            type: 'IMPRESSION',
            items: impressions,
          },
          presses: {
            type: 'PRESS',
            items: presses,
          },
          includeImpressions: impressions.length > 0,
          includePresses: presses.length > 0,
        },
        context: {
          requireAuth: true,
        },
      })
    } catch (error) {
      logger().error(
        'Failed to flush recommendation tracking',
        {
          impressionsCount: impressions.length,
          pressesCount: presses.length,
        },
        error,
      )
      throw error
    }
  }, [trackRecommendationMutation, isLoggedIn])

  React.useEffect(() => {
    const flushInterval = setInterval(() => {
      flushTrackRecommendations().catch((error) => {
        logger().error('Failed to flush recommendations in interval', error)
      })
    }, 10000)

    return () => {
      clearInterval(flushInterval)
      flushTrackRecommendations().catch((error) => {
        logger().error('Failed to flush recommendations on cleanup', error)
      })
    }
  }, [flushTrackRecommendations])

  const trackRecommendation = React.useCallback(
    async (trackInput: TrackInput) => {
      if (!isLoggedIn) {
        return
      }

      const throttledTracker = trackInput.type === 'PRESS' ? throttledPresses.current : throttledImpressions.current

      const items: RecommendationTrack[] = trackInput.items
        .filter((item) => {
          if (!item?.id || throttledTracker.tracked.has(item.id)) {
            return false
          }

          if (!validateUuid(item?.id)) {
            logger().error('Attempted to track a Recommendation with an improper uuid', { item, trackInput })
            return false
          }

          throttledTracker.tracked.add(item.id)
          return true
        })
        .map((item) => {
          const id = item.id
          const timestamp = new Date().toISOString()
          if (trackInput.type === 'PRESS') {
            track('Recommendation Press', { id, timestamp })
          }
          return { id, timestamp }
        })

      throttledTracker.throttled.push(...items)
    },
    [isLoggedIn, track],
  )

  return { trackRecommendation, flushTrackRecommendations }
}
